import React, { FC } from 'react';

import { BatchProgress } from '@/hooks/useBatchProcessor';
import { Bar } from './Bar';
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from './icons';

type BatchProgressBarProps = {
  progress: BatchProgress;
  className?: string;
  showDetails?: boolean;
};

const ProcessingProgress: FC<BatchProgressBarProps> = ({
  progress,
  showDetails,
}) => {
  const {
    currentBatch,
    totalBatches,
    processedItems,
    totalItems,
    percentage,
    successfulBatches,
    errors,
  } = progress;

  return (
    <div className="p-4 border border-gray-700 dark:bg-gray-50 rounded-lg">
      <h4 className="text-sm font-medium mb-3">Processing Progress</h4>
      <div className="space-y-2">
        {showDetails && (
          <div className="flex justify-between text-sm">
            <span>
              Batch {currentBatch} of {totalBatches}
            </span>
            <span>
              {processedItems} / {totalItems} items
            </span>
          </div>
        )}
        <Bar loading={false} value={percentage / 100}>
          {Math.round(percentage)}%
        </Bar>
        {showDetails && (
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
            <span>Successful batches: {successfulBatches}</span>
            <span>Failed batches: {errors.length}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const CompletionSummary: FC<BatchProgressBarProps> = ({ progress }) => {
  const { totalBatches, processedItems, errors, successfulBatches } = progress;

  return (
    <div
      className={`p-4 rounded-lg ${
        errors.length === 0
          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
          : successfulBatches > 0
            ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
      }`}
    >
      <div className="flex items-center mb-2">
        {progress.errors.length === 0 ? (
          <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
        ) : progress.successfulBatches > 0 ? (
          <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" />
        ) : (
          <XCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
        )}
        <div
          className={`text-sm font-medium ${
            errors.length === 0
              ? 'text-green-800 dark:text-green-200'
              : successfulBatches > 0
                ? 'text-yellow-800 dark:text-yellow-200'
                : 'text-red-800 dark:text-red-200'
          }`}
        >
          {errors.length === 0
            ? 'All Items Assigned Successfully!'
            : successfulBatches > 0
              ? 'Partial Success - Some Batches Failed'
              : 'Assignment Failed'}
        </div>
      </div>
      <div className="text-sm space-y-1 text-gray-800 dark:text-gray-200">
        <div>Total items processed: {processedItems}</div>
        <div>
          Successful batches: {successfulBatches} / {totalBatches}
        </div>
      </div>
      {errors.length > 0 && (
        <div className="text-red-800 dark:text-red-400 mt-2">
          <strong>Failed batches:</strong> {errors.length}
          <details className="mt-1">
            <summary className="cursor-pointer hover:underline">
              View error details
            </summary>
            <ul className="mt-1 ml-4 text-xs">
              {errors.map((error, index) => (
                <li key={index} className="list-disc">
                  {error}
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}
    </div>
  );
};

export const BatchProgressBar: FC<BatchProgressBarProps> = ({
  progress,
  className = '',
  showDetails = true,
}) => {
  const { isProcessing, totalBatches } = progress;

  if (!isProcessing && totalBatches === 0) {
    return null;
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {isProcessing && (
        <ProcessingProgress progress={progress} showDetails={showDetails} />
      )}
      {!isProcessing && totalBatches > 0 && (
        <CompletionSummary progress={progress} showDetails={showDetails} />
      )}
    </div>
  );
};
